import { useMemo, useState } from 'react'
import { Autocomplete, Box, TextField, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import type { RegionData, RegionSetData } from '@/models/Regions'
import { inputSx } from '../styles'

type RegionOption = RegionData | (RegionSetData & { isLocationSet: true })

const GROUP_PREVIEW_COUNT = 5

export function formatGroupLocationsPreview(
  regionIds: number[],
  regionNameById?: Map<number, string>,
): string {
  const base = `Adds ${regionIds.length} locations`
  if (!regionNameById) return base

  const previewNames = regionIds
    .slice(0, GROUP_PREVIEW_COUNT)
    .map((id) => regionNameById.get(id))
    .filter((name): name is string => Boolean(name))
  if (previewNames.length === 0) return base

  const remaining = regionIds.length - previewNames.length
  return remaining > 0
    ? `${base}: ${previewNames.join(', ')} +${remaining} more`
    : `${base}: ${previewNames.join(', ')}`
}

export const getAsciiName = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()

export function filterRegionOptions(
  options: RegionOption[],
  inputValue: string,
): RegionOption[] {
  const trimmed = inputValue.toLowerCase().trim()
  if (!trimmed) return []
  const asciiInput = getAsciiName(trimmed)

  const filtered = options.filter((option) => {
    if (option.ascii_name) {
      if (option.ascii_name.toLowerCase().includes(asciiInput)) return true
    } else if (getAsciiName(option.name).includes(asciiInput)) {
      return true
    }
    if (
      'additional_names' in option &&
      option.additional_names &&
      option.additional_names.length > 0
    ) {
      return option.additional_names.some((additionalName: string) =>
        getAsciiName(additionalName).toLowerCase().includes(asciiInput),
      )
    }
    return false
  })

  return filtered.sort((a, b) => {
    const aIsSet = 'isLocationSet' in a
    const bIsSet = 'isLocationSet' in b

    // Location sets come first
    if (aIsSet && !bIsSet) return -1
    if (bIsSet && !aIsSet) return 1

    const aName = (a.ascii_name || a.name).toLowerCase()
    const bName = (b.ascii_name || b.name).toLowerCase()
    const aExact = aName === asciiInput
    const bExact = bName === asciiInput
    if (aExact && !bExact) return -1
    if (bExact && !aExact) return 1
    const aStarts = aName.startsWith(asciiInput)
    const bStarts = bName.startsWith(asciiInput)
    if (aStarts && !bStarts) return -1
    if (bStarts && !aStarts) return 1
    return aName.length - bName.length
  })
}

export function renderRegionOption(
  props: React.HTMLAttributes<HTMLLIElement>,
  option: RegionOption,
  regionNameById?: Map<number, string>,
) {
  // Render location set with GROUP badge
  if ('isLocationSet' in option) {
    const regionSet = option as RegionSetData & { isLocationSet: true }
    return (
      <li {...props} key={`set-${regionSet.id}`}>
        <div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="span"
              sx={{
                bgcolor: '#1C3045',
                color: 'white',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: '0.75em',
                fontWeight: 'bold',
              }}
            >
              GROUP
            </Box>
            <strong>{regionSet.name}</strong>
          </Box>
          <div style={{ color: 'gray', fontSize: '0.85em' }}>
            {formatGroupLocationsPreview(regionSet.region_ids, regionNameById)}
          </div>
          {regionSet.additional_names && regionSet.additional_names.length > 0 && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.85em',
                fontStyle: 'italic',
              }}
            >
              Also known as: {regionSet.additional_names.join(', ')}
            </Typography>
          )}
        </div>
      </li>
    )
  }

  const region = option as RegionData
  const hierarchy = [
    region.country,
    region.region,
    region.council,
    region.major,
    region.suburb,
  ]
    .filter(Boolean)
    .join(' > ')

  return (
    <li {...props} key={region.id}>
      <div>
        <strong>{region.name}</strong>
        {hierarchy && (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', fontSize: '0.9em' }}
          >
            {hierarchy}
          </Typography>
        )}
        {region.additional_names && region.additional_names.length > 0 && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.85em',
              fontStyle: 'italic',
            }}
          >
            Also known as: {region.additional_names.join(', ')}
          </Typography>
        )}
      </div>
    </li>
  )
}

interface RegionAutocompleteProps {
  regions: RegionData[]
  locationSets?: RegionSetData[]
  value: RegionData[]
  onChange: (regions: RegionData[]) => void
  excludeIds?: number[]
  placeholder?: string
  loading?: boolean
  textFieldSx?: SxProps<Theme>
  /** Called when a region-set option is selected. Receives the set's region IDs. */
  onRegionSetSelect?: (regionIds: number[]) => void
  id?: string
  required?: boolean
  error?: boolean
  helperText?: string
}

export default function RegionAutocomplete({
  regions,
  locationSets,
  value,
  onChange,
  excludeIds,
  placeholder = 'Type to search locations...',
  loading,
  textFieldSx,
  onRegionSetSelect,
  id,
  required,
  error,
  helperText,
}: RegionAutocompleteProps) {
  const [inputValue, setInputValue] = useState('')

  const regionNameById = useMemo(
    () => new Map(regions.map((r) => [r.id, r.name])),
    [regions],
  )

  const filteredRegions = excludeIds
    ? regions.filter((r) => !excludeIds.includes(r.id))
    : regions

  const options: RegionOption[] = [
    // Location sets first, marked distinctly
    ...(locationSets || []).map((set: RegionSetData) => ({
      ...set,
      isLocationSet: true as const,
    })),
    // Then individual regions
    ...filteredRegions,
  ]

  return (
    <Autocomplete
      id={id}
      multiple
      loading={loading}
      value={value}
      onChange={(_, newValues, reason, details) => {
        if (
          onRegionSetSelect &&
          reason === 'selectOption' &&
          details?.option &&
          'region_ids' in details.option &&
          !('country' in details.option)
        ) {
          const regionSet = details.option as RegionSetData & {
            isLocationSet: true
          }
          onRegionSetSelect(regionSet.region_ids)
          setInputValue('')
          return
        }
        const regionValues = newValues.filter(
          (v): v is RegionData =>
            v !== undefined &&
            v !== null &&
            (typeof v === 'string'
              ? false
              : 'country' in v || !('region_ids' in v)),
        )
        onChange(regionValues)
      }}
      options={options}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option
        if ('isLocationSet' in option) return `${option.name} (Location Set)`
        return (option as RegionData).name || ''
      }}
      isOptionEqualToValue={(option, val) => {
        if ('isLocationSet' in option) return false
        return option.id === (val as RegionData).id
      }}
      filterOptions={(opts, params) =>
        filterRegionOptions(opts, params.inputValue)
      }
      renderOption={(props, option) =>
        renderRegionOption(props, option, regionNameById)
      }
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      freeSolo
      noOptionsText={
        inputValue.length > 0
          ? 'No matching locations found.'
          : 'Start typing to search locations...'
      }
      slotProps={{
        chip: {
          role: undefined,
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          sx={textFieldSx}
          slotProps={{
            input: { ...params.InputProps, sx: inputSx },
          }}
        />
      )}
    />
  )
}
